/**
 * h5/src/ws/envelope.ts
 * WS 信封编解码 + R1 DTU 字段守护
 *
 * R1 红线：主动删除 payload 中的 dtu_id/serial/raw_hex 字段，
 * 防御服务端剥离失败时 DTU 原始数据透传到 store。
 */

import { logger } from '@/utils/logger'
import type { Envelope } from './types'

// ─── DTU 禁止透传的字段名（R1 红线）────────────────────────────────────────

/**
 * DTU 敏感字段黑名单（R1 红线）
 * 这些字段绝不能出现在前端 store 中
 */
const DTU_FIELDS = ['dtu_id', 'serial', 'raw_hex', 'dtu_data', 'device_id', 'dtu_raw'] as const
type DtuFieldName = (typeof DTU_FIELDS)[number]

// ─── msg_id 生成（简版 UUID v4，不引入 uuid 包）──────────────────────────────

/**
 * 生成简版 UUID v4
 * 格式：xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx
 */
function generateMsgId(): string {
  const hex = '0123456789abcdef'
  let uuid = ''
  for (let i = 0; i < 36; i++) {
    if (i === 8 || i === 13 || i === 18 || i === 23) {
      uuid += '-'
    } else if (i === 14) {
      uuid += '4'
    } else if (i === 19) {
      uuid += hex[(Math.random() * 4) | 8]
    } else {
      uuid += hex[(Math.random() * 16) | 0]
    }
  }
  return uuid
}

// ─── R1 守护：DTU 字段剥离 ───────────────────────────────────────────────────

/**
 * 从 payload 中剥离 DTU 相关字段（R1 红线守护）
 *
 * 行为：
 * - 检测 payload 中是否存在 DTU 黑名单字段
 * - 若存在，记录 logger.warn（仅字段名，不输出值）
 * - 返回剥离后的 payload 副本
 *
 * @param payload 原始 payload（可能来自服务端推送）
 * @returns 剥离 DTU 字段后的安全 payload
 */
export function stripDtuFields(payload: unknown): unknown {
  if (payload === null || payload === undefined || typeof payload !== 'object') {
    return payload
  }
  if (Array.isArray(payload)) {
    return payload
  }

  const obj = payload as Record<string, unknown>
  const stripped: DtuFieldName[] = []

  for (const field of DTU_FIELDS) {
    if (Object.prototype.hasOwnProperty.call(obj, field)) {
      stripped.push(field)
    }
  }

  if (stripped.length === 0) {
    return payload
  }

  // 记录被剥离的字段名（不输出值，避免 DTU 原始数据进日志）
  logger.warn('[R1] DTU 字段已从 payload 中剥离', { stripped_fields: stripped })

  const safe = { ...obj }
  for (const field of stripped) {
    delete safe[field]
  }
  return safe
}

// ─── 信封编码 ────────────────────────────────────────────────────────────────

/**
 * 信封编码选项
 */
export interface EncodeOptions {
  /** 自定义 msg_id（默认自动生成 UUID v4） */
  msgId?: string
  /** 是否要求服务端回 ack（默认 false） */
  ackRequired?: boolean
  /** 服务端时间戳（客户端上行通常为 0） */
  serverTs?: number
}

/**
 * 构建并序列化 WS 信封
 *
 * @param type    事件类型（如 'heartbeat' / 'ack'）
 * @param payload 业务数据
 * @param opts    可选配置
 * @returns JSON 字符串
 */
export function encode(type: string, payload: unknown = {}, opts?: EncodeOptions): string {
  const envelope: Envelope = {
    type,
    payload,
    timestamp: Date.now(),
    msg_id: opts?.msgId ?? generateMsgId(),
    ack_required: opts?.ackRequired ?? false,
    server_ts: opts?.serverTs ?? 0,
  }
  return JSON.stringify(envelope)
}

// ─── 信封解码 ────────────────────────────────────────────────────────────────

/**
 * 必需字段列表（用于解码校验）
 * 注：timestamp 和 msg_id 为可选兼容字段——后端旧版 auth_ok/HELLO 不携带，
 * 缺失时使用默认值（0 / ''）而非拒绝，保证 AUTHENTICATING → CONNECTED 状态转换正常。
 */
const REQUIRED_FIELDS: (keyof Envelope)[] = ['type', 'payload']

/**
 * 解析 WS 信封 JSON
 *
 * 行为：
 * - JSON 解析失败 → logger.warn + 返回 null
 * - 缺少 type / payload 字段 → logger.warn + 返回 null
 * - timestamp / msg_id 缺失时使用默认值（向后兼容旧协议）
 * - 成功解析后自动对 payload 执行 stripDtuFields（R1）
 *
 * @param raw 原始 WebSocket 消息字符串
 * @returns 解析后的信封（payload 已脱敏），或 null（解析失败）
 */
export function decode(raw: string): Envelope | null {
  // 1. JSON 解析
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    logger.warn('[envelope] JSON 解析失败', { raw_length: raw.length })
    return null
  }

  // 2. 必须是对象
  if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
    logger.warn('[envelope] 信封不是对象类型')
    return null
  }

  const obj = parsed as Record<string, unknown>

  // 3. 校验必需字段（type 必填；payload 缺失时默认 {}，兼容旧版 pong/auth 帧）
  if (!('type' in obj)) {
    logger.warn('[envelope] 信封缺少必需字段', { missing_fields: ['type'] })
    return null
  }
  if (!('payload' in obj)) {
    logger.debug('[envelope] payload 缺失，使用空对象', { type: obj['type'] })
  }
  const safePayload = stripDtuFields(
    obj['payload'] !== undefined && obj['payload'] !== null ? obj['payload'] : {},
  )

  // 5. 构造类型安全的 Envelope
  //    timestamp / msg_id 可选：旧版后端 auth_ok 不携带，缺失时用默认值兼容
  const envelope: Envelope = {
    type: String(obj['type'] ?? ''),
    payload: safePayload,
    timestamp: Number(obj['timestamp'] ?? 0),
    msg_id: String(obj['msg_id'] ?? ''),
    ack_required: Boolean(obj['ack_required'] ?? false),
    server_ts: Number(obj['server_ts'] ?? 0),
  }

  return envelope
}
